// @ts-strict
import type { Accidental, NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import type Note from '../uTheory.Notation/note';
import Interval from '../uTheory.Notation/interval';
import { getAllNotesInRange } from './randomNoteGenerators';
import NoteHolder from '../uTheory.Notation/noteHolder';
import IntervalQuestion from './intervalQuestion';
import type { IntervalComparison } from './intervalQuestion';
import type Clef from '../uTheory.Notation/clef';
import rejectDuplicates from './rejectDuplicates';

type IntervalWithNote = {
  note: Note;
  interval: Interval;
  harmonic: boolean;
};

export type IntervalGenerationOptions = {
  lowestNote: Note | NoteName;
  highestNote: Note | NoteName;
  acceptableFirstNoteAccidentals: Accidental[];
  acceptableSecondNoteAccidentals: Accidental[];
  mustFitSingleClef: boolean;
  acceptableClefs?: (Clef | ClefName)[];
};

const defaultIntervalGenerationOptions: IntervalGenerationOptions = {
  acceptableFirstNoteAccidentals: ['b', '', '#'],
  acceptableSecondNoteAccidentals: ['bb', 'b', '', '#', 'x'],
  mustFitSingleClef: false,
  lowestNote: 'E2',
  highestNote: 'C6',
};

export function getIntervalsInRange(
  /**
   * When string, can be simply "P4" or specify melodic or harmonic as
   * "P4/melodic" or "P4/harmonic"
   */
  intervalOrIntervalStrings: (string | Interval)[],
  options: Partial<IntervalGenerationOptions> = {}
): IntervalWithNote[] {
  const fullOptions = {
    ...defaultIntervalGenerationOptions,
    ...options,
  };
  const { lowestNote, highestNote } = fullOptions;
  const notes = getAllNotesInRange(
    lowestNote,
    highestNote,
    fullOptions.acceptableFirstNoteAccidentals
  );
  const intervalInfos = intervalOrIntervalStrings.map((interval) => {
    if (interval instanceof Interval) return { interval, harmonic: false };
    const [intervalName, intervalType] = interval.split('/');
    return {
      interval: new Interval(intervalName as IntervalName),
      harmonic: !!intervalType?.includes('harmonic'),
    };
  });

  return notes
    .map((note) =>
      intervalInfos.map(({ interval, harmonic }): IntervalWithNote | false => {
        const note2 = note.getNoteAt(interval);
        if (!note2) return false;
        // Is its accidental acceptable?
        if (!fullOptions.acceptableSecondNoteAccidentals.includes(note2.accidental)) return false;
        // Is it in chromatic range?
        if (note2.isLessThan(lowestNote) || note2.isGreaterThan(highestNote)) return false;
        // Is it in letter name range?
        if (
          note2.isLessThanIgnoreAccidental(lowestNote) ||
          note2.isGreaterThanIgnoreAccidental(highestNote)
        ) {
          return false;
        }

        const preferedClefs = new NoteHolder([note, note2]).prefersClef();
        // Does it fit in one clef?
        if (fullOptions.mustFitSingleClef && preferedClefs.length === 0) {
          return false;
        }

        // Does it fit in at least one of the acceptable clefs?
        if (fullOptions.acceptableClefs) {
          const matchedClef = preferedClefs.find((preferedClef) =>
            fullOptions.acceptableClefs?.find((acceptableClef) => acceptableClef === preferedClef)
          );
          if (!matchedClef) return false;
        }

        // It's all good -- send it back!
        return { note, interval, harmonic };
      })
    )
    .flat()
    .filter((intPair): intPair is IntervalWithNote => !!intPair);
}

interface IntervalQuestionGenerationOptions extends Partial<IntervalGenerationOptions> {
  comparisonType: IntervalComparison;
  excludeEnharmonicDuplicatesIfPossible: boolean;
}

const defaultIntervalQGOptions: IntervalQuestionGenerationOptions = {
  ...defaultIntervalGenerationOptions,
  comparisonType: 'notes',
  excludeEnharmonicDuplicatesIfPossible: false,
};

export function randomIntervalQuestions(
  numberOfQuestions: number,
  /**
   * When string, can be simply "P4" or specify melodic or harmonic as
   * "P4/melodic" or "P4/harmonic"
   */
  intervals: (string | Interval)[],
  options: Partial<IntervalQuestionGenerationOptions> = {}
): IntervalQuestion[] {
  const fullOptions: IntervalQuestionGenerationOptions = {
    ...defaultIntervalQGOptions,
    ...options,
  };
  let allIntervals = getIntervalsInRange(intervals, fullOptions);

  if (options.excludeEnharmonicDuplicatesIfPossible) {
    const intervalsWithEnharmonicsEliminated = allIntervals.filter(
      ({ note: interval1note1, interval: interval1 }, i, arr) =>
        arr.slice(0, i).find(({ note: interval2note1, interval: interval2 }) => {
          const interval1note2 = interval1note1.getNoteAt(interval1);
          const interval2note2 = interval2note1.getNoteAt(interval2);
          if (!interval1note2 || !interval2note2) throw Error(`Invalid interval pairs`);
          const nh1 = new NoteHolder([interval1note1, interval1note2]);
          const nh2 = new NoteHolder([interval2note1, interval2note2]);
          return !nh1.equals(nh2);
        })
    );
    if (intervalsWithEnharmonicsEliminated.length >= numberOfQuestions) {
      allIntervals = intervalsWithEnharmonicsEliminated;
    }
  }
  const allIntervalQuestions = allIntervals.map(
    ({ interval, note, harmonic }) => new IntervalQuestion(interval, note, { harmonic, ...options })
  );

  return rejectDuplicates(allIntervalQuestions, numberOfQuestions);
}
